import { useMemo } from "react";
import { getCandidates, getStarterCandidates } from "../../domain/candidates";
import { pluralize } from "../../domain/pluralize";
import type { RecoveryOption } from "../../domain/recovery";
import {
  EMPTY_SELECTION,
  type Locale,
  type SelectionState,
} from "../../domain/types";
import { RecipeResults } from "./RecipeResults";

const STARTER_CANDIDATES = getStarterCandidates();

interface Props {
  recovery: RecoveryOption | null;
  locale: Locale;
  onClear: () => void;
  onRecover: (selection: SelectionState, recipeId?: string) => void;
  returnFocusRecipeId: string | null;
}

export function RecoveryResults({
  recovery,
  locale,
  onClear,
  onRecover,
  returnFocusRecipeId,
}: Props) {
  const previewSelection = recovery?.nextSelection ?? EMPTY_SELECTION;
  const previewCandidates = useMemo(
    () =>
      recovery
        ? getCandidates(previewSelection).slice(0, 3)
        : STARTER_CANDIDATES,
    [recovery, previewSelection],
  );

  return (
    <>
      <section className="no-results" aria-labelledby="results-title">
        <p className="eyebrow">No exact fit</p>
        <h2 id="results-title">These choices do not meet in one recipe yet.</h2>
        {recovery ? (
          <>
            <p>
              Remove <strong>{recovery.label}</strong> to restore{" "}
              {recovery.restoredCount}{" "}
              {pluralize(recovery.restoredCount, locale, {
                en: ["recipe", "recipes"],
              })}
              .
            </p>
            <button
              className="primary-button"
              onClick={() => onRecover(recovery.nextSelection)}
            >
              Remove {recovery.label}
            </button>
          </>
        ) : (
          <>
            <p>No single removal restores an exact result.</p>
            <button className="primary-button" onClick={onClear}>
              Clear all
            </button>
          </>
        )}
      </section>

      <section
        className="results-section recovery-results"
        aria-labelledby="recovery-results-title"
      >
        <div className="section-heading">
          <div>
            <span className="section-kicker">
              {recovery ? "Relaxed preview" : "Start over"}
            </span>
            <h2 id="recovery-results-title">
              {recovery ? `Ideas without ${recovery.label}` : "Starter ideas"}
            </h2>
          </div>
          <span>{previewCandidates.length} shown</span>
        </div>
        <p className="recovery-results__note">
          {recovery
            ? `These ideas do not match ${recovery.label}. Choose one to continue without it.`
            : "These are starter ideas, not matches for your current choices."}
        </p>
        <RecipeResults
          candidates={previewCandidates}
          selection={previewSelection}
          locale={locale}
          onOpen={(recipeId) => onRecover(previewSelection, recipeId)}
          limit={previewCandidates.length}
          returnFocusRecipeId={returnFocusRecipeId}
        />
      </section>
    </>
  );
}
